import { Feature } from 'geojson';
import {
    DataOptions,
    GeoLevel,
    Indicator,
    MeanValuesByYear,
} from '../../types';
import { format } from '../../helpers/format';
import { useTranslation } from 'react-i18next';
import { extent } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';

export type TooltipFeatures = {
    kommun?: Feature;
    hovered?: Feature;
};

function SparkLines(props: {
    line1: { x: number; y: number }[];
    line2?: { x: number; y: number }[];
    currentYear: number;
    currentValue: number;
}) {
    const padding = {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2,
    };

    const xExtent = extent(
        [...props.line1, ...(props.line2 ?? [])],
        (d) => d.x
    ) as [number, number];
    const xScale = scaleLinear()
        .domain(xExtent)
        .range([padding.left, 60 - padding.right]);

    const yExtent = extent(
        [...props.line1, ...(props.line2 ?? [])],
        (d) => d.y
    ) as [number, number];
    const yScale = scaleLinear()
        .domain(yExtent)
        .range([20 - padding.top, padding.bottom]);
    const l = line<{ x: number; y: number }>()
        .x((d) => xScale(d.x))
        .y((d) => yScale(d.y))
        .defined((d) => d.y !== null);

    return (
        <svg width={60} height={20}>
            <path
                d={l(props.line2 ?? []) ?? ''}
                fill="none"
                stroke="#d65a8d"
                strokeOpacity="0.75"
                strokeWidth="1"
            />
            <path
                d={l(props.line1) ?? ''}
                fill="none"
                stroke="black"
                strokeOpacity="0.5"
                strokeWidth="1"
            />
            <circle
                cx={xScale(props.currentYear)}
                cy={yScale(props.currentValue)}
                r={1.5}
            />
        </svg>
    );
}

export function Tooltip(props: {
    tooltipFeatures: TooltipFeatures;
    geoLevel: GeoLevel;
    dataOptions: DataOptions;
    year: number;
    meanByYear: MeanValuesByYear;
    years: number[];
}) {
    const { t, i18n } = useTranslation();

    function HeaderRow() {
        return (
            <tr>
                <th className="sticky top-0"></th>
                <th className="sticky top-0">{t('indikator')}</th>
                <th></th>
                <th className="sticky top-0">{t('tooltip-omradet')}</th>
                <th className="sticky top-0" style={{ color: '#d65a8d' }}>
                    {t('mean-value')}
                </th>
                <th className="sticky top-0">{t('tooltip-kommunen')}</th>
            </tr>
        );
    }

    const { hovered, kommun } = props.tooltipFeatures;

    const uniqoptions = Array.from(
        new Set(Object.values(props.dataOptions.indicators))
    ).filter((v) => v) as Indicator[];

    function getHeader(
        geoLevel: GeoLevel,
        hovered: Feature | undefined,
        kommun: Feature | undefined
    ) {
        if (geoLevel === 'kommun') {
            return hovered?.properties?.kommunnamn;
        }

        return `${hovered?.properties?.name} (${hovered?.properties?.kommunnamn})`;
    }

    return (
        <div className="card border shadow-xl bg-base-100 text-base-content p-3">
            <h2 className="text-lg mb-2">
                {getHeader(props.geoLevel, hovered, kommun)} {props.year}
            </h2>

            <table className="table table-compact w-full relative overflow-x-auto">
                <thead>
                    <HeaderRow />
                </thead>
                <tbody>
                    {uniqoptions.map((indicator, i) => {
                        if (!hovered?.properties?.values[indicator.id]) {
                            return;
                        }
                        return (
                            <tr key={indicator.id}>
                                <td>{i + 1}</td>
                                <td>
                                    {indicator.label[i18n.language] ??
                                        indicator.label['sv']}
                                    ,{' '}
                                    {indicator.subLabel[i18n.language] ??
                                        indicator.subLabel['sv']}
                                </td>
                                <td>
                                    <SparkLines
                                        line1={props.years.map((year) => {
                                            return {
                                                x: year,
                                                y: hovered?.properties?.values[
                                                    indicator.id
                                                ][year],
                                            };
                                        })}
                                        line2={props.years.map((year) => {
                                            return {
                                                x: year,
                                                y: props.meanByYear[
                                                    indicator.id
                                                ][year]!,
                                            };
                                        })}
                                        currentYear={props.year}
                                        currentValue={
                                            hovered?.properties?.values[
                                                indicator.id
                                            ][props.year]
                                        }
                                    />
                                </td>
                                <td>
                                    {format(indicator.unitFormat)(
                                        hovered?.properties?.values[
                                            indicator.id
                                        ][props.year]
                                    )}{' '}
                                    {indicator.unitDisplay ?? ''}
                                </td>
                                <td>
                                    {props.meanByYear[indicator.id]?.[
                                        props.year
                                    ] && (
                                        <>
                                            {format(indicator.unitFormat)(
                                                props.meanByYear[indicator.id][
                                                    props.year
                                                ]!
                                            )}{' '}
                                            {indicator.unitDisplay ?? ''}
                                        </>
                                    )}
                                </td>
                                <td>
                                    {kommun &&
                                        kommun.properties?.values[
                                            indicator.id
                                        ]?.[props.year] && (
                                            <>
                                                {format(indicator.unitFormat)(
                                                    kommun.properties?.values[
                                                        indicator.id
                                                    ][props.year]
                                                )}{' '}
                                                {indicator.unitDisplay ?? ''}
                                            </>
                                        )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
